import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Button,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Menu,
  Stack,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { PROJECT_TYPES } from "./ProjectTypes";

import {
  TABLE_STYLES,
  TABLE_ICON_BLUE_SX,
  TABLE_ROW_HOVER_SX,
} from "../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../theme/filterstyles";

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const PHASE_SELECT_SX = {
  minWidth: { xs: 180, sm: 220 },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "transparent",
    },
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
};

export default function ProjectsTable({
  projects,
  visibleIds,
  allCurrentSelected,
  someCurrentSelected,
  onTogglePage,
  isSelected,
  toggleOne,

  navigate,
  renderCityState,

  PHASES,
  setProjects,
  api,
  projectTypeFilter,
  onProjectTypeFilterChange,
  projectPhaseFilter,
  onProjectPhaseFilterChange,
  projectManagerFilter,
  onProjectManagerFilterChange,
  managerOptions,
  managersLoading,

  setSelectedProject,
  setProfileOpen,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const getProjectTypeLabel = (projectType) =>
    PROJECT_TYPES.find((type) => type.value === projectType)?.label ||
    projectType ||
    "—";

  const openFilterMenu = (event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  };

  const closeFilterMenu = () => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  };

  const filteredProjectTypeOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return PROJECT_TYPES;
    return PROJECT_TYPES.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch]);

  const filteredProjectManagerOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return managerOptions;
    return managerOptions.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, managerOptions]);

  const filteredPhaseOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return PHASES;
    return PHASES.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, PHASES]);

  const renderFilterHeader = ({
    label,
    filterKey,
    active = false,
  }) => {
    const minWidthByFilterKey = {
      project_type: 138,
      project_manager: 164,
      project_phase: 220,
    };

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%" }}
      >
        <Button
          variant="text"
          onClick={(event) => openFilterMenu(event, filterKey)}
          sx={{
            ...HEADER_TRIGGER_SX,
            minWidth: minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
            color: active ? "#648315" : "#00336C",
            border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
            backgroundColor: "rgba(255,255,255,0.92)",
            "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
          }}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
        >
          {label}
        </Button>
      </Stack>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "project_type") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search project types")}
          <MenuItem
            selected={!projectTypeFilter}
            onClick={() => {
              onProjectTypeFilterChange("");
              closeFilterMenu();
            }}
          >
            All Types
          </MenuItem>
          {filteredProjectTypeOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={projectTypeFilter === option.value}
              onClick={() => {
                onProjectTypeFilterChange(option.value);
                closeFilterMenu();
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "project_manager") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search project managers")}
          <MenuItem
            selected={!projectManagerFilter}
            onClick={() => {
              onProjectManagerFilterChange("");
              closeFilterMenu();
            }}
          >
            All Managers
          </MenuItem>
          {managersLoading && managerOptions.length === 0 && (
            <MenuItem disabled>(Loading...)</MenuItem>
          )}
          {filteredProjectManagerOptions.map((option) => (
            <MenuItem
              key={option}
              selected={projectManagerFilter === option}
              onClick={() => {
                onProjectManagerFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "project_phase") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search phases")}
          <MenuItem
            selected={!projectPhaseFilter}
            onClick={() => {
              onProjectPhaseFilterChange("");
              closeFilterMenu();
            }}
          >
            All Phases
          </MenuItem>
          {filteredPhaseOptions.map((option) => (
            <MenuItem
              key={option}
              selected={projectPhaseFilter === option}
              onClick={() => {
                onProjectPhaseFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 1120, md: 1260 }, borderCollapse: "separate", borderSpacing: 0 }}>
        {/* ===== HEAD ===== */}
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                padding="checkbox"
                sx={HEAD_CELL_SX}
              >
                <Checkbox
                  indeterminate={someCurrentSelected && !allCurrentSelected}
                  checked={visibleIds.length > 0 && allCurrentSelected}
                  onChange={onTogglePage}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>

              {["Project", "Project Type", "Company", "Location", "Project Manager", "Phase", "Profile"].map(
                (header) => (
                  <TableCell
                    key={header}
                    align="center"
                    sx={HEAD_CELL_SX}
                  >
                    {header === "Project Type"
                      ? renderFilterHeader({
                          label: header,
                          filterKey: "project_type",
                          active: Boolean(projectTypeFilter),
                        })
                      : header === "Project Manager"
                        ? renderFilterHeader({
                            label: header,
                            filterKey: "project_manager",
                            active: Boolean(projectManagerFilter),
                          })
                        : header === "Phase"
                          ? renderFilterHeader({
                              label: header,
                              filterKey: "project_phase",
                              active: Boolean(projectPhaseFilter),
                            })
                          : header}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>

        {/* ===== BODY ===== */}
          <TableBody sx={{ background: "white" }}>
            {projects.map((project, idx) => {
              const idStr = project?.id != null ? String(project.id) : null;
              const checked = idStr ? isSelected(idStr) : false;

              return (
                <TableRow
                  key={project.id ?? idx}
                  sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: idx % 2 === 0 ? "#fafafa" : "white", "&:hover": { backgroundColor: "#eef2f7" } }}
                >
                  {/* Checkbox */}
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      disabled={!idStr}
                      checked={checked}
                      onChange={() => idStr && toggleOne(idStr)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  {/* Project Name */}
                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    <Tooltip title={project.project_name || ""}>
                      <Button
                        variant="text"
                        sx={TABLE_STYLES.clickableCell}
                        onClick={() => navigate(`/projects/${project.id}`)}
                      >
                        {project.project_name || "—"}
                      </Button>
                    </Tooltip>
                  </TableCell>

                  {/* Project Type */}
                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    <Tooltip title={getProjectTypeLabel(project.project_type)}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {getProjectTypeLabel(project.project_type)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Company */}
                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    <Tooltip title={project.company || ""}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {project.company || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Location */}
                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    {renderCityState(project)}
                  </TableCell>

                  {/* Project Manager */}
                  <TableCell align="center" sx={TABLE_STYLES.bodyCell}>
                    <Tooltip title={project.project_manager || ""}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {project.project_manager || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Phase */}
                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      minWidth: 180,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      sx={PHASE_SELECT_SX}
                      value={project.project_phase}
                      onChange={async (e) => {
                        const newPhase = e.target.value;

                        try {
                          await api.patch(`/projects/${project.id}/phase`, {
                            phase: newPhase,
                          });

                          setProjects((prev) =>
                            prev.map((p) =>
                              p.id === project.id
                                ? { ...p, project_phase: newPhase }
                                : p,
                            ),
                          );
                        } catch (err) {
                          alert("Could not update project phase.");
                        }
                      }}
                    >
                      {PHASES.map((p) => (
                        <MenuItem key={p} value={p}>
                          {p}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  {/* Profile */}
                  <TableCell align="center">
                    <Tooltip title="View Project Profile">
                      <IconButton
                        onClick={() => {
                          setSelectedProject(project);
                          setProfileOpen(true);
                        }}
                      >
                        <AssignmentOutlinedIcon
                          sx={TABLE_ICON_BLUE_SX}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Empty state */}
            {projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  No projects found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {renderFilterMenu()}
    </>
  );
}
